import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Image,
  Input,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import FileUpload from "views/admin/events/components/FileUpload";
import {
  addHomeHappening,
  deleteHomeHappening,
  getHomeContent,
  updateHomeHappening,
  updateHomeHero,
  getImageUrl,
} from "Api/apiService";

const emptyHero = {
  eyebrow: "",
  title: "",
  subtitle: "",
  ctaLabel: "",
  ctaRoute: "reserve",
  showImage: false,
  image: "",
  imageFile: null,
  imageName: "",
};

const ctaRoutes = [
  { label: "Home", value: "home" },
  { label: "Menu", value: "menu" },
  { label: "Drink Menu", value: "drinkMenu" },
  { label: "Brunch Menu", value: "brunchMenu" },
  { label: "Happy Hour", value: "happyHour" },
  { label: "Game Specials", value: "gameSpecials" },
  { label: "Reservations", value: "reserve" },
  { label: "Events", value: "events" },
  { label: "Engage", value: "engage" },
  { label: "Staff", value: "staff" },
  { label: "Grove Black Card", value: "blackCard" },
  { label: "Sky Lounge", value: "privateBooking" },
  { label: "Merch", value: "merch" },
];

const emptyHappening = {
  day: "",
  title: "",
  details: "",
  time: "",
  image: "",
  imageFile: null,
  imageName: "",
};

const parseDetails = (value) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const normalizeHeroSlide = (slide = {}) => ({
  ...emptyHero,
  ...slide,
  showImage: Boolean(slide?.showImage),
  image: slide?.image || "",
  imageFile: null,
  imageName: "",
});

export default function MobileHome() {
  const [heroSlides, setHeroSlides] = useState([emptyHero]);
  const [happenings, setHappenings] = useState([]);
  const [newHappening, setNewHappening] = useState(emptyHappening);
  const [editingHappeningId, setEditingHappeningId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingHero, setSavingHero] = useState(false);
  const [savingHappening, setSavingHappening] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [heroFileErrors, setHeroFileErrors] = useState({});
  const toast = useToast();
  const happeningFormRef = useRef(null);

  const textColor = useColorModeValue("navy.700", "white");
  const labelColor = useColorModeValue("secondaryGray.600", "whiteAlpha.700");

  const refreshContent = async () => {
    setLoading(true);
    try {
      const response = await getHomeContent();
      const content = response?.data?.homeContent;
      if (Array.isArray(content?.heroSlides) && content.heroSlides.length > 0) {
        setHeroSlides(content.heroSlides.map(normalizeHeroSlide));
      } else if (content?.hero) {
        setHeroSlides([normalizeHeroSlide(content.hero)]);
      } else {
        setHeroSlides([emptyHero]);
      }
      setHappenings(Array.isArray(content?.happenings) ? content.happenings : []);
      setHeroFileErrors({});
    } catch (error) {
      console.error("Failed to load home content", error);
      toast({
        title: "Unable to load home content.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshContent();
  }, []);

  useEffect(() => {
    if (editingHappeningId && happeningFormRef.current) {
      happeningFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [editingHappeningId]);

  const handleHeroChange = (index, key) => (event) => {
    const value = event.target.value;
    setHeroSlides((prev) =>
      prev.map((slide, slideIndex) =>
        slideIndex === index ? { ...slide, [key]: value } : slide
      )
    );
  };

  const handleHeroToggle = (index) => (event) => {
    const checked = event.target.checked;
    setHeroSlides((prev) =>
      prev.map((slide, slideIndex) =>
        slideIndex === index ? { ...slide, showImage: checked } : slide
      )
    );
  };

  const handleHeroImageChange = (index) => (file) => {
    setHeroSlides((prev) =>
      prev.map((slide, slideIndex) =>
        slideIndex === index
          ? {
              ...slide,
              imageFile: file || null,
              imageName: file ? file.name : "",
              showImage: file ? true : slide.showImage,
            }
          : slide
      )
    );
  };

  const setHeroFileError = (index) => (message) => {
    setHeroFileErrors((prev) => ({
      ...prev,
      [index]: message,
    }));
  };

  const addHeroSlide = () => {
    setHeroSlides((prev) => [...prev, emptyHero]);
  };

  const removeHeroSlide = (index) => {
    setHeroSlides((prev) =>
      prev.length === 1 ? prev : prev.filter((_, idx) => idx !== index)
    );
  };

  const handleHappeningChange = (key) => (event) => {
    setNewHappening((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleHappeningFileChange = (file) => {
    setNewHappening((prev) => ({
      ...prev,
      imageFile: file || null,
      imageName: file ? file.name : "",
    }));
  };

  const isHeroValid = useMemo(
    () =>
      heroSlides.length > 0 &&
      heroSlides.every(
        (hero) =>
          hero.eyebrow.trim() &&
          hero.title.trim() &&
          hero.subtitle.trim() &&
          hero.ctaLabel.trim() &&
          hero.ctaRoute.trim() &&
          (!hero.showImage ||
            Boolean((hero.image && hero.image.trim()) || hero.imageFile))
      ),
    [heroSlides]
  );

  const getHeroValidationErrors = () =>
    heroSlides.flatMap((hero, index) => {
      const missing = [];
      if (!hero.eyebrow.trim()) missing.push("eyebrow");
      if (!hero.title.trim()) missing.push("title");
      if (!hero.subtitle.trim()) missing.push("subtitle");
      if (!hero.ctaLabel.trim()) missing.push("cta label");
      if (!hero.ctaRoute.trim()) missing.push("cta route");
      if (
        hero.showImage &&
        !((hero.image && hero.image.trim()) || hero.imageFile)
      ) {
        missing.push("image");
      }
      if (missing.length === 0) return [];
      return [`Slide ${index + 1}: ${missing.join(", ")}`];
    });

  const isHappeningValid = useMemo(
    () =>
      newHappening.day.trim() &&
      newHappening.title.trim() &&
      newHappening.time.trim() &&
      (newHappening.imageFile || (newHappening.image || "").trim()),
    [newHappening]
  );

  const saveHero = async () => {
    if (!isHeroValid) {
      const errors = getHeroValidationErrors();
      toast({
        title: "Fill all hero fields before saving.",
        description: errors.length > 0 ? errors.join(" • ") : undefined,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSavingHero(true);
    try {
      await updateHomeHero({ heroSlides });
      toast({
        title: "Hero updated.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      refreshContent();
    } catch (error) {
      console.error("Failed to update hero", error);
      toast({
        title: "Unable to update hero.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingHero(false);
    }
  };

  const addHappening = async () => {
    if (!isHappeningValid) {
      toast({
        title: "Fill required fields before adding.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSavingHappening(true);
    try {
      await addHomeHappening({
        day: newHappening.day.trim(),
        title: newHappening.title.trim(),
        time: newHappening.time.trim(),
        imageFile: newHappening.imageFile,
        details: parseDetails(newHappening.details || ""),
      });
      toast({
        title: "Happening added.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      setNewHappening(emptyHappening);
      setFileError(null);
      refreshContent();
    } catch (error) {
      console.error("Failed to add happening", error);
      toast({
        title: "Unable to add happening.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingHappening(false);
    }
  };

  const startEditHappening = (item) => {
    setEditingHappeningId(item._id);
    setNewHappening({
      day: item.day || "",
      title: item.title || "",
      time: item.time || "",
      details: Array.isArray(item.details) ? item.details.join("\n") : "",
      image: item.image || "",
      imageFile: null,
      imageName: "",
    });
    setFileError(null);
  };

  const cancelEditHappening = () => {
    setEditingHappeningId(null);
    setNewHappening(emptyHappening);
    setFileError(null);
  };

  const saveHappening = async () => {
    if (!isHappeningValid) {
      toast({
        title: "Fill required fields before saving.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSavingHappening(true);
    try {
      await updateHomeHappening({
        happeningId: editingHappeningId,
        day: newHappening.day.trim(),
        title: newHappening.title.trim(),
        time: newHappening.time.trim(),
        imageFile: newHappening.imageFile,
        image: newHappening.image,
        details: parseDetails(newHappening.details || ""),
      });
      toast({
        title: "Happening updated.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      cancelEditHappening();
      refreshContent();
    } catch (error) {
      console.error("Failed to update happening", error);
      toast({
        title: "Unable to update happening.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSavingHappening(false);
    }
  };

  const removeHappening = async (happeningId) => {
    try {
      await deleteHomeHappening(happeningId);
      toast({
        title: "Happening deleted.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      if (editingHappeningId === happeningId) {
        cancelEditHappening();
      }
      refreshContent();
    } catch (error) {
      console.error("Failed to delete happening", error);
      toast({
        title: "Unable to delete happening.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const resolveImageUrl = (path) => {
    if (!path) {
      return "";
    }
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return getImageUrl(path);
  };

  const isEditingHappening = Boolean(editingHappeningId);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="20px" mb="20px">
        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Mobile App Hero
            </Text>
            <Text fontSize="14px" color={labelColor}>
              Update the hero copy and call-to-action used on the mobile home
              screen.
            </Text>
            <Divider />
            <Stack spacing="14px">
              {heroSlides.map((hero, index) => (
                <Box
                  key={`hero-${index}`}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="16px"
                >
                  <Flex justify="space-between" align="center" mb="12px">
                    <Text fontWeight="700" color={textColor}>
                      Hero Slide {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeHeroSlide(index)}
                      isDisabled={heroSlides.length === 1}
                    >
                      Remove
                    </Button>
                  </Flex>
                  <Stack spacing="12px">
                    <FormControl>
                      <FormLabel color={labelColor}>Eyebrow</FormLabel>
                      <Input
                        value={hero.eyebrow}
                        onChange={handleHeroChange(index, "eyebrow")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Title</FormLabel>
                      <Input
                        value={hero.title}
                        onChange={handleHeroChange(index, "title")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Subtitle</FormLabel>
                      <Input
                        value={hero.subtitle}
                        onChange={handleHeroChange(index, "subtitle")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>CTA Label</FormLabel>
                      <Input
                        value={hero.ctaLabel}
                        onChange={handleHeroChange(index, "ctaLabel")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>CTA Route</FormLabel>
                      <Select
                        value={hero.ctaRoute}
                        onChange={handleHeroChange(index, "ctaRoute")}
                      >
                        {ctaRoutes.map(route => (
                          <option key={route.value} value={route.value}>
                            {route.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel color={labelColor} mb="0">
                        Show Hero Image
                      </FormLabel>
                      <Switch
                        isChecked={hero.showImage}
                        onChange={handleHeroToggle(index)}
                        colorScheme="blue"
                      />
                    </FormControl>
                    {hero.showImage && hero.image && !hero.imageFile ? (
                      <Box>
                        <Image
                          src={resolveImageUrl(hero.image)}
                          alt="Hero preview"
                          maxH="200px"
                          borderRadius="12px"
                        />
                        <Text fontSize="sm" color={labelColor}>
                          {hero.image}
                        </Text>
                      </Box>
                    ) : null}
                    {hero.showImage ? (
                      <FormControl>
                        <FormLabel color={labelColor}>Hero Image</FormLabel>
                        <FileUpload
                          error={heroFileErrors[index]}
                          setError={setHeroFileError(index)}
                          onFileSelect={handleHeroImageChange(index)}
                          onFileRemove={() => handleHeroImageChange(index)(null)}
                        />
                      </FormControl>
                    ) : null}
                  </Stack>
                </Box>
              ))}
              <Button variant="outline" onClick={addHeroSlide}>
                Add Hero Slide
              </Button>
              <Button
                colorScheme="blue"
                onClick={saveHero}
                isLoading={savingHero}
              >
                Save Hero
              </Button>
            </Stack>
          </Flex>
        </Card>

        <Box ref={happeningFormRef}>
          <Card p="24px">
            <Flex direction="column" gap="16px">
              <Text fontSize="22px" fontWeight="700" color={textColor}>
                {isEditingHappening ? "Edit Happening" : "Add Happening"}
              </Text>
              <Text fontSize="14px" color={labelColor}>
                {isEditingHappening
                  ? "Update the selected happening card."
                  : "Add a new featured card for the mobile home screen."}
              </Text>
              <Divider />
              <Stack spacing="14px">
                <FormControl>
                  <FormLabel color={labelColor}>Day</FormLabel>
                  <Input
                    value={newHappening.day}
                    onChange={handleHappeningChange("day")}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color={labelColor}>Title</FormLabel>
                  <Input
                    value={newHappening.title}
                    onChange={handleHappeningChange("title")}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color={labelColor}>Time</FormLabel>
                  <Input
                    value={newHappening.time}
                    onChange={handleHappeningChange("time")}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color={labelColor}>Image Upload</FormLabel>
                  <FileUpload
                    error={fileError}
                    setError={setFileError}
                    onFileSelect={handleHappeningFileChange}
                    onFileRemove={() => handleHappeningFileChange(null)}
                  />
                  {isEditingHappening &&
                  newHappening.image &&
                  !newHappening.imageFile ? (
                    <Box mt="8px">
                      <Image
                        src={resolveImageUrl(newHappening.image)}
                        alt="Current happening"
                        maxH="120px"
                        borderRadius="12px"
                      />
                      <Text fontSize="sm" color={labelColor}>
                        Current image: {newHappening.image}
                      </Text>
                    </Box>
                  ) : null}
                </FormControl>
                <FormControl>
                  <FormLabel color={labelColor}>Details (one per line)</FormLabel>
                  <Textarea
                    value={newHappening.details}
                    onChange={handleHappeningChange("details")}
                    rows={4}
                  />
                </FormControl>
                <Button
                  colorScheme={isEditingHappening ? "blue" : "green"}
                  onClick={isEditingHappening ? saveHappening : addHappening}
                  isLoading={savingHappening}
                >
                  {isEditingHappening ? "Save Changes" : "Add Happening"}
                </Button>
                {isEditingHappening ? (
                  <Button variant="ghost" onClick={cancelEditHappening}>
                    Cancel Edit
                  </Button>
                ) : null}
              </Stack>
            </Flex>
          </Card>
        </Box>
      </SimpleGrid>

      <Card p="24px">
        <Flex direction="column" gap="16px">
          <Flex align="center" justify="space-between">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Current Happenings
            </Text>
            <Button onClick={refreshContent} isLoading={loading}>
              Refresh
            </Button>
          </Flex>
          <Divider />
          {happenings.length === 0 ? (
            <Text color={labelColor}>
              No happenings yet. Add one to feature it on the mobile home
              screen.
            </Text>
          ) : (
            <Stack spacing="12px">
              {happenings.map((item) => (
                <Flex
                  key={item._id}
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "flex-start", md: "center" }}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="16px"
                  gap="12px"
                >
                  <Box>
                    <Text fontWeight="700" color={textColor}>
                      {item.title}
                    </Text>
                    <Text fontSize="sm" color={labelColor}>
                      {item.day} · {item.time}
                    </Text>
                    <Text fontSize="sm" color={labelColor}>
                      {item.image}
                    </Text>
                    {Array.isArray(item.details) && item.details.length > 0 ? (
                      <Stack mt="8px" spacing="4px">
                        {item.details.map((detail) => (
                          <Text key={detail} fontSize="sm" color={labelColor}>
                            {detail}
                          </Text>
                        ))}
                      </Stack>
                    ) : null}
                  </Box>
                  <Flex gap="8px" wrap="wrap">
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => startEditHappening(item)}
                    >
                      Edit
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={() => removeHappening(item._id)}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Flex>
              ))}
            </Stack>
          )}
        </Flex>
      </Card>
    </Box>
  );
}
